import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ProductFilters from './ProductFilters';
import ProductListTable from './ProductListTable';
import Pagination from './Pagination';
import ProductDetailView, { mapFormDataToApiPayload } from './ProductDetailView';
import {
  getProductDetail,
  listProducts,
  updateProduct,
  updateProductOptions,
  getCurrentUserRole,
  listCategories,
  listVendors,
  bulkMoveToDraft,
  bulkDeleteProducts,
  bulkPushToPendingApproval,
  pushToPendingApproval,
  approveProduct,
  rejectProduct,
  requestChangesProduct,
  deleteProduct,
  moveToDraft,
  moveToActive,
  moveToUnlisted,
  exportProductsToCSV,
  exportAdminProductLogs,
} from '../../services/shipturtleApi';
import { useProductsChildRoute } from './productsNavigation';
import { StaleDataBanner } from '../feedback/DataFallback';
import { classifyError } from '../../utils/errorTaxonomy';
import './ManageProducts.css';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';

// ─── Lightweight toast ────────────────────────────────────────────────────────
function Toast({ toasts, onDismiss }) {
  if (!toasts || toasts.length === 0) return null;
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '12px 18px', borderRadius: 8, minWidth: 260, maxWidth: 400,
          background: t.type === 'error' ? '#b91c1c' : t.type === 'warning' ? '#92400e' : '#065f46',
          color: '#fff', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.18)'
        }}>
          <span style={{ flex: 1 }}>{t.message}</span>
          <button onClick={() => onDismiss(t.id)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);
  const dismiss = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);
  return { toasts, show, dismiss };
}
// ─────────────────────────────────────────────────────────────────────────────

// Memoized component that only re-renders when products or related props change
const ProductListSection = React.memo(({ 
  products, 
  loading, 
  error,
  onRetry,
  paginatedProducts,
  onProductAction,
  isReadOnlyAuditor,
  userRole,
  userId,
  productListRef,
  startIndex,
  endIndex,
  filteredProducts,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  selectedProducts,
  selectAllMode,
  excludedIds,
  effectiveSelectedCount,
  onSelectAllGlobal,
  onUnselectAll,
  onProductSelect,
  onSelectAll,
  onRowClick
}) => {
  console.log('ProductListSection render:', {
    selectedProducts,
    selectAllMode,
    excludedCount: excludedIds?.length,
    onProductSelect: typeof onProductSelect,
    onSelectAll: typeof onSelectAll,
    paginatedProductsCount: paginatedProducts?.length
  });

  const excludedCount = (excludedIds || []).length;

  return (
    <>
      <div className="resultsSummary">
        <p>
          Showing {startIndex + 1}-{Math.min(endIndex, totalCount || (filteredProducts ? filteredProducts.length : 0))} of {totalCount || (filteredProducts ? filteredProducts.length : 0)} products
        </p>
      </div>

      {/* Selection banner — appears on any selection, and is the only place the
          cross-page "select everything matching the filters" action lives. */}
      {(selectedProducts.length > 0 || selectAllMode) && (
        <div className="selectionBanner">
          <div className="selectionBannerContent">
            <span className="selectionCount">
              {selectAllMode
                ? (excludedCount > 0
                    ? `${effectiveSelectedCount} of ${totalCount} Products Selected.`
                    : `All ${totalCount} Products Selected.`)
                : `(${selectedProducts.length}) Products Selected.`}
            </span>
            <button className="selectionBannerButton" onClick={onUnselectAll}>
              Unselect Products
            </button>
            {!selectAllMode && totalCount > selectedProducts.length && (
              <>
                <span className="selectionBannerDivider">/</span>
                <button className="selectionBannerButton" onClick={onSelectAllGlobal}>
                  Select All {totalCount} Products
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Product list container */}
      <div 
        ref={productListRef}
        style={{ 
          position: 'relative', 
          minHeight: '200px',
        }}
      >
        {loading && products.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <div className="mainLoadingSpinner"></div>
          </div>
        )}
        
        <ProductListTable 
          products={paginatedProducts}
          onProductAction={onProductAction}
          onRowClick={onRowClick}
          isReadOnly={isReadOnlyAuditor}
          userRole={userRole}
          isLoading={loading && products.length === 0} // Only show table loader for initial load, not for actions
          // Only when there is nothing left to show; with rows on screen the
          // staleness notice above carries the message instead.
          error={products.length === 0 ? error : null}
          onRetry={onRetry}
          userId={userId}
          selectedProducts={selectedProducts}
          selectAllMode={selectAllMode}
          excludedIds={excludedIds}
          onProductSelect={(productId, isSelected) => {
            onProductSelect(productId, isSelected);
          }}
          onSelectAll={(isSelected) => {
            onSelectAll(isSelected);
          }}
        />
      </div>
      
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
});

ProductListSection.displayName = 'ProductListSection';

function ManageProducts() {
  // The open product lives in the URL: /products/manage/:productId
  const {
    childId: routeProductId,
    openChild: openProduct,
    closeChild: closeProduct,
    isCurrentChild,
  } = useProductsChildRoute('manage');

  // Get user info from Redux store
  const userTypeRaw = useSelector(state => state.user?.user_type);
  const userId = useSelector(state => state.user?.id);

  // Toast notifications
  const { toasts, show: showToast, dismiss: dismissToast } = useToast();

  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // When the rows on screen were last confirmed current, for the staleness notice.
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isReadOnlyAuditor, setIsReadOnlyAuditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [isExportingAdminLogs, setIsExportingAdminLogs] = useState(false);
  
  // Bulk selection state.
  //
  // Two mutually exclusive modes:
  //   * normal      — `selectedProducts` holds explicitly ticked ids, accumulated
  //                   across pages.
  //   * select-all  — `selectAllMode` is on and the selection is "everything
  //                   matching the current filters", resolved server-side at action
  //                   time. `selectedProducts` is empty; `excludedIds` holds the rows
  //                   the user unticked afterwards ("select all, minus a few").
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [excludedIds, setExcludedIds] = useState([]);

  // Ref for the scrollable product list container
  const productListRef = useRef(null);
  
  // Filter and search state
  const [filters, setFilters] = useState({
    globalSearch: '',
    status: 'all',
    category: 'all',
    vendor: 'all',
    channel: 'all',
    inventory: { min: '', max: '' },
    dateRange: 'all',
    productStatus: 'all',
    linkingStatus: 'all',
    mappingStatus: 'all'
  });

  // Debounced filters for API calls (to prevent API call on every keystroke)
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const debounceTimerRef = useRef(null);
  
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25); // fallback client-side page size
    const [serverPagination, setServerPagination] = useState(null); // set when API returns pagination metadata
  
  // Detail view state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailViewMode, setDetailViewMode] = useState(null);
  useTaraTerminalFocus('open_product', selectedProduct && {
    product_id: selectedProduct.id,
    title: selectedProduct.title,
    mode: detailViewMode,
  });
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [saveReason, setSaveReason] = useState('');
  
  // RBAC: Determine if user is Vendor
  const isVendor = userRole === 'vendor';

  // Map the UI filter state to the backend's snake_case query params.
  //
  // Deliberately shared between the list fetch and "select all matching filters":
  // a select-all is resolved server-side by re-running these same filters, so if
  // the two mappings ever diverged a bulk action would silently mutate a different
  // set than the admin was looking at. One definition, no drift.
  const buildBackendFilters = useCallback(() => {
    const apiParams = {};

    if (debouncedFilters.status && debouncedFilters.status !== 'all') {
      apiParams.status = debouncedFilters.status;
    } else {
      // Default: exclude unlisted and pending_approval products in ManageProducts
      // Only show draft and approved products
      apiParams.status_exclude = 'unlisted,pending_approval';
    }
    if (debouncedFilters.productStatus && debouncedFilters.productStatus !== 'all') {
      // Catalog status filter → backend `terminal_status` (active/draft/archived/unlisted/deleted).
      // (The old `product_state` param was silently ignored by the v2 list API.)
      apiParams.terminal_status = debouncedFilters.productStatus;
    }
    if (debouncedFilters.category && debouncedFilters.category !== 'all') {
      apiParams.category_id = debouncedFilters.category;
    }
    if (debouncedFilters.vendor && debouncedFilters.vendor !== 'all') {
      apiParams.vendor_id = debouncedFilters.vendor;
    }
    if (debouncedFilters.channel && debouncedFilters.channel !== 'all') {
      apiParams.channel = debouncedFilters.channel;
    }
    if (debouncedFilters.globalSearch) {
      apiParams.search = debouncedFilters.globalSearch;
    }
    if (debouncedFilters.inventory?.min) {
      apiParams.inventory_min = debouncedFilters.inventory.min;
    }
    if (debouncedFilters.inventory?.max) {
      apiParams.inventory_max = debouncedFilters.inventory.max;
    }
    // Linking (are store ids on file?) and mapping (is sync on?) are separate
    // axes — a product can be linked but unmapped, which is the case worth
    // finding. Both flow through here, so a select-all bulk action resolves
    // server-side against exactly the set the admin is looking at.
    if (debouncedFilters.linkingStatus && debouncedFilters.linkingStatus !== 'all') {
      apiParams.linking_status = debouncedFilters.linkingStatus;
    }
    if (debouncedFilters.mappingStatus && debouncedFilters.mappingStatus !== 'all') {
      apiParams.mapping_status = debouncedFilters.mappingStatus;
    }

    // Handle date range filter
    if (debouncedFilters.dateRange && debouncedFilters.dateRange !== 'all') {
      const now = new Date();
      let dateFrom = null;

      switch (debouncedFilters.dateRange) {
        case 'today':
          dateFrom = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          dateFrom = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          dateFrom = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          break;
      }

      if (dateFrom) {
        apiParams.date_from = dateFrom.toISOString();
      }
    }

    return apiParams;
  }, [debouncedFilters]);

  // Function to load/reload products
  const loadProducts = async () => {
    try {
      // Don't reset scroll on filter changes
      const wasLoading = loading;
      setLoading(true);
      setError(null);

      console.log('Fetching products from API with filters:', debouncedFilters);

      // Build API params from DEBOUNCED filters
      const apiParams = {
        // Server-side pagination now uses limit & offset only
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        ...buildBackendFilters(),
      };

      // Fetch products from API with filters
      const response = await listProducts(apiParams);
      
      console.log('API Response:', response);
      
      if (response && response.products) {
        console.log('Products loaded:', response.products.length);
        // `terminal_status` (catalog: draft/active/unlisted/...) and `state` (approval:
        // pending/active) already come straight from the API via `...p` — never
        // renamed or aliased. `approval_status` is the one derived display field: the
        // workflow label the "Status" column shows (pending_approval/rejected/etc.),
        // kept clearly named apart from the two real backend fields so it can never be
        // mistaken for — or silently overwrite — either of them.
        const mapped = response.products.map(p => ({
          ...p,
          approval_status: p.state === 'pending'
            ? 'pending_approval'
            : (p.terminal_status || p.state || 'draft'),
        }));
        setProducts(mapped);
        // Use server-side pagination metadata when provided
        if (response.pagination) {
          setServerPagination(response.pagination);
        } else {
          setServerPagination(null);
        }
        setLastLoadedAt(Date.now());
      } else {
        console.error('Invalid response format:', response);
        setError(classifyError(new Error('Invalid response format')));
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      // Keep the classified description. The old string embedded the browser's
      // own "Failed to fetch", which named no cause the user could act on.
      setError(err && err.classified ? err.classified : classifyError(err));
      // Deliberately keeps whatever rows are already on screen: losing the list
      // someone is working through is worse than showing figures a minute old.
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch user role from backend on mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        console.log('User role from backend:', roleData);
        setUserRole(roleData.role);
        setIsReadOnlyAuditor(userTypeRaw === 'read_only_auditor');
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('vendor');
        setIsReadOnlyAuditor(false);
      }
    };
    fetchUserRole();
  }, [userTypeRaw]);
  
  // Fetch categories and vendors for filters
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const categoriesResponse = await listCategories();
        if (categoriesResponse && categoriesResponse.categories) {
          setCategories(categoriesResponse.categories);
        }
        
        if (userRole === 'admin') {
          const vendorsResponse = await listVendors();
          if (vendorsResponse && vendorsResponse.results) {
            setVendors(vendorsResponse.results);
          }
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    
    if (userRole) {
      fetchFilterOptions();
    }
  }, [userRole]);
  
  // Debounce filter changes for text inputs (wait 500ms after user stops typing)
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer for debounced update
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500); // 500ms delay
    
    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters]);
  
  // Fetch products based on user role and DEBOUNCED filters
  useEffect(() => {
    if (userRole) {
      loadProducts();
    }
  }, [userRole, userId, debouncedFilters, currentPage]); // Use debouncedFilters instead of filters
  
  // Reset product list container scroll to top when filters or page change
  useEffect(() => {
    if (productListRef.current && !loading) {
      productListRef.current.scrollTop = 0;
    }
  }, [filters, currentPage, products]);
  
  // Backend filtering is now handled by loadProducts()
  // Products are already filtered by the API
  const filteredProducts = products;

  // Memoize calculated pagination values to prevent unnecessary recalculations
  const { totalPages, startIndex, endIndex, paginatedProducts, totalCount } = useMemo(() => {
    // If server provided pagination metadata, use server-side paging
    if (serverPagination) {
      const pageSize = Number(serverPagination.page_size) || itemsPerPage;
      const totalPagesCalc = Number(serverPagination.total_pages) || 1;
      const start = (currentPage - 1) * pageSize;
      const end = start + (filteredProducts ? filteredProducts.length : 0);
      // Server already returns only the items for the current page
      return {
        totalPages: totalPagesCalc,
        startIndex: start,
        endIndex: end,
        paginatedProducts: filteredProducts,
        totalCount: Number(serverPagination.total_count) || (filteredProducts ? filteredProducts.length : 0)
      };
    }

    // Fallback to client-side pagination
    const total = Math.ceil(filteredProducts.length / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    const paginated = filteredProducts.slice(start, end);
    
    return {
      totalPages: total,
      startIndex: start,
      endIndex: end,
      paginatedProducts: paginated,
      totalCount: filteredProducts.length
    };
  }, [filteredProducts, currentPage, itemsPerPage, serverPagination]);
  
  // How many products a bulk action would actually touch. In select-all mode the
  // set is "everything matching the filters" minus whatever was unticked, so the
  // count is derived rather than a list length.
  const effectiveSelectedCount = selectAllMode
    ? Math.max(0, totalCount - excludedIds.length)
    : selectedProducts.length;

  const hasSelection = effectiveSelectedCount > 0;

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
    // Clear the selection: a select-all is resolved from the filters at action
    // time, so leaving it active across a filter change would silently retarget it
    // at a different set of products than the one that was selected.
    setSelectedProducts([]);
    setSelectAllMode(false);
    setExcludedIds([]);
  }, []);
  
  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);



  // Re-fetches the currently-open detail modal's product after a status action, so it
  // reflects the new terminal_status/state instead of staying stale until re-opened.
  const refreshSelectedProductIfOpen = useCallback(async (productId) => {
    setSelectedProduct(prev => {
      if (!prev || prev.id !== productId) return prev;
      getProductDetail(productId).then(response => {
        if (response?.product) {
          setSelectedProduct({ ...response.product, missing_fields: response.missing_fields || [] });
        }
      }).catch(() => {});
      return prev;
    });
  }, []);

  const handleError = useCallback((err, context) => {
    console.error(`${context}:`, err);
    const status = err?.status || (err?.message?.includes('403') ? 403 : err?.message?.includes('404') ? 404 : 0);
    if (status === 403) showToast("You don't have permission to perform this action", 'error');
    else if (status === 404) showToast('Product not found', 'error');
    else showToast(`Something went wrong, please try again (${err.message || 'unknown error'})`, 'error');
  }, [showToast]);

  // Loads and shows the product named in the URL. A product that can't be
  // loaded sends the user back to the list rather than leaving a dead URL.
  const showProduct = useCallback(async (productId) => {
    try {
      setLoading(true);
      const response = await getProductDetail(productId);
      if (!isCurrentChild(productId)) return; // user moved on while it loaded
      if (response && response.product) {
        setSelectedProduct({ ...response.product, missing_fields: response.missing_fields || [] });
        setDetailViewMode('edit');
      } else {
        showToast('Failed to load product details', 'error');
        closeProduct();
      }
    } catch (err) {
      if (!isCurrentChild(productId)) return;
      handleError(err, 'Error fetching product details');
      closeProduct();
    } finally {
      setLoading(false);
    }
  }, [isCurrentChild, closeProduct, showToast, handleError]);

  // Handle product actions
  const handleProductAction = useCallback(async (action, productId, additionalData = {}) => {
    switch (action) {
      case 'edit':
      case 'view':
        openProduct(productId);
        break;

      case 'delete': {
        try {
          setLoading(true);
          await deleteProduct(productId, additionalData.reason || '');
          showToast('Product deleted successfully');
          // Optimistic: remove from local list rather than full reload
          setProducts(prev => prev.filter(p => p.id !== productId));
          await loadProducts();
        } catch (err) {
          handleError(err, 'Error deleting product');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'draft': {
        try {
          setLoading(true);
          await moveToDraft(productId, additionalData.reason || '');
          showToast('Product moved to draft');
          await loadProducts();
          await refreshSelectedProductIfOpen(productId);
        } catch (err) {
          handleError(err, 'Error moving product to draft');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'active': {
        try {
          setLoading(true);
          const response = await moveToActive(productId, additionalData.reason || '');
          if (response?.errors?.length) {
            showToast(`Move to active failed: ${response.errors[0]}`, 'error');
          } else {
            showToast('Move to active submitted — awaiting admin approval');
          }
          await loadProducts();
          await refreshSelectedProductIfOpen(productId);
        } catch (err) {
          handleError(err, 'Error moving product to active');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'unlisted': {
        try {
          setLoading(true);
          const response = await moveToUnlisted(productId, additionalData.reason || '');
          if (response?.errors?.length) {
            showToast(`Move to unlisted failed: ${response.errors[0]}`, 'error');
          } else {
            showToast('Move to unlisted submitted — awaiting admin approval');
          }
          await loadProducts();
          await refreshSelectedProductIfOpen(productId);
        } catch (err) {
          handleError(err, 'Error unlisting product');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'submit_for_approval': {
        try {
          setLoading(true);
          await pushToPendingApproval(productId);
          showToast('Product submitted for approval');
          // Update local cache entry instead of full reload
          setProducts(prev => prev.map(p => p.id === productId ? { ...p, approval_status: 'pending_approval', state: 'pending' } : p));
          await loadProducts();
        } catch (err) {
          handleError(err, 'Error submitting for approval');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'approve': {
        try {
          setLoading(true);
          const response = await approveProduct(productId);
          showToast('Product approved');
          // Update local state from the real response (nested under `product`); real
          // fields (state/terminal_status) come straight from the server, approval_status
          // is re-derived the same way loadProducts() derives it.
          const updated = response?.product;
          setProducts(prev => prev.map(p => {
            if (p.id !== productId) return p;
            const state = updated?.state || 'active';
            const terminal_status = updated?.terminal_status || p.terminal_status;
            return {
              ...p,
              state,
              terminal_status,
              approval_status: state === 'pending' ? 'pending_approval' : (terminal_status || state),
            };
          }));
          await loadProducts();
        } catch (err) {
          handleError(err, 'Error approving product');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'reject': {
        const reason = additionalData.reason || window.prompt('Enter rejection reason:');
        if (!reason) return;
        try {
          setLoading(true);
          await rejectProduct(productId, reason);
          showToast('Product rejected');
          setProducts(prev => prev.map(p => p.id === productId ? { ...p, approval_status: 'rejected' } : p));
          await loadProducts();
        } catch (err) {
          handleError(err, 'Error rejecting product');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'request_changes': {
        const message = additionalData.message || window.prompt('Enter message for vendor:');
        if (!message) return;
        try {
          setLoading(true);
          await requestChangesProduct(productId, message);
          showToast('Changes requested from vendor');
          setProducts(prev => prev.map(p => p.id === productId ? { ...p, approval_status: 'request_changes' } : p));
          await loadProducts();
        } catch (err) {
          handleError(err, 'Error requesting changes');
        } finally {
          setLoading(false);
        }
        break;
      }

      case 'duplicate':
        showToast('Duplicate is not available yet', 'warning');
        break;
      case 'publish':
      case 'activate':
      case 'deactivate':
      case 'restock':
      case 'manage_channels':
        showToast(`Action "${action}" is not available in this view`, 'warning');
        break;
      default:
        console.log('Unknown action:', action);
    }
  }, [loadProducts, openProduct, handleError, showToast, refreshSelectedProductIfOpen]);

  // The detail view follows the URL: entering /manage/:id opens that product
  // (row click, deep link, Forward), leaving it (Back, ×, tab switch) closes it.
  useEffect(() => {
    if (!routeProductId) {
      setSelectedProduct(null);
      setDetailViewMode(null);
      return;
    }
    if (userRole) showProduct(routeProductId);
  }, [routeProductId, userRole, showProduct]);

  // Handle saving product changes
  const handleSaveProduct = useCallback(async (updatedProductData, reason) => {
    // If reason is provided, save directly (called from ProductDetailView's internal modal)
    if (reason) {
      try {
        setLoading(true);
        
        const apiPayload = mapFormDataToApiPayload(updatedProductData, reason);
        
        // Save options first if present
        if (updatedProductData.options) {
          try {
            await updateProductOptions(updatedProductData.id, { options: updatedProductData.options });
          } catch (optErr) {
            console.warn('[ManageProducts] Options update failed (non-blocking):', optErr);
          }
        }
        
        const response = await updateProduct(updatedProductData.id, apiPayload);
        
        if (response && response.product) {
          showToast('Product saved and moved to pending approval!');
          // Back to the list the product was opened from (not the approval
          // queue — vendors can't act there), refreshed so it reflects the move.
          closeProduct(updatedProductData.id);
          loadProducts();
        } else {
          throw new Error('Failed to update product');
        }
      } catch (error) {
        console.error('Error saving product:', error);
        showToast(updatedProductData._pushToPendingApproval
          ? `Error pushing to pending approval: ${error.message || 'please try again'}`
          : `Error saving product: ${error.message || 'please try again'}`, 'error');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Otherwise, show modal for reason (legacy flow, if still needed)
    setPendingSaveData(updatedProductData);

    // If this is a push to pending approval action, set a default reason message and close detail view
    if (updatedProductData._pushToPendingApproval) {
      setSaveReason('Product pushed to pending approval with updates');
      // Close the ProductDetailView first
      setSelectedProduct(null);
      setDetailViewMode(null);
    }
    
    setShowReasonModal(true);
  }, [showToast, closeProduct, loadProducts]);
  
  // Confirm save with reason
  const handleConfirmSave = async () => {
    if (!saveReason || saveReason.trim() === '') {
      showToast('A reason is required to save changes', 'warning');
      return;
    }
    
    const isPushToPendingApproval = pendingSaveData?._pushToPendingApproval;
    
    try {
      setLoading(true);
      setShowReasonModal(false);
      
      // Save options first if present
      if (pendingSaveData.options) {
        try {
          await updateProductOptions(pendingSaveData.id, { options: pendingSaveData.options });
        } catch (optErr) {
          console.warn('[ManageProducts] Options update failed (non-blocking):', optErr);
        }
      }

      const apiPayload = mapFormDataToApiPayload(pendingSaveData, saveReason.trim());
      const response = await updateProduct(pendingSaveData.id, apiPayload);
      
      if (response && response.product) {
        showToast('Product saved and moved to pending approval!');
        closeProduct(pendingSaveData.id);
        setPendingSaveData(null);
        setSaveReason('');
        loadProducts();
      } else {
        throw new Error('Failed to update product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      showToast(isPushToPendingApproval
        ? `Error pushing to pending approval: ${error.message || 'please try again'}`
        : `Error saving product: ${error.message || 'please try again'}`, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel save
  const handleCancelSave = useCallback(() => {
    setShowReasonModal(false);
    setPendingSaveData(null);
    setSaveReason('');
  }, []);
  
  // Handle closing detail view (× / Cancel): back up to the list. Scoped to the
  // product it was opened for, so the detail view's delayed close after a save
  // can't close a different product opened in the meantime.
  const handleCloseDetailView = useCallback((productId) => closeProduct(productId), [closeProduct]);
  
  // Bulk action handlers
  const handleProductSelect = (productId, isSelected) => {
    // In select-all mode the selection is defined by the filters, not a list, so a
    // single row is deselected by *excluding* it. Without this the checkbox would
    // refuse to untick and the action would still hit every matching row.
    if (selectAllMode) {
      setExcludedIds(prev => {
        const s = new Set(prev || []);
        if (isSelected) s.delete(productId);
        else s.add(productId);
        return Array.from(s);
      });
      return;
    }

    setSelectedProducts(prev => {
      const s = new Set(prev || []);
      if (isSelected) s.add(productId);
      else s.delete(productId);
      return Array.from(s);
    });
  };

  const handleSelectAll = (isSelected) => {
    const pageIds = (paginatedProducts || []).map(p => p.id);

    if (selectAllMode) {
      if (isSelected) {
        // Re-include every row on this page.
        setExcludedIds(prev => {
          const s = new Set(prev || []);
          pageIds.forEach(id => s.delete(id));
          return Array.from(s);
        });
      } else {
        // Unticking the header drops out of select-all entirely rather than
        // excluding the page one row at a time — "unselect all" is what this
        // gesture means once everything is selected.
        setSelectAllMode(false);
        setExcludedIds([]);
        setSelectedProducts([]);
      }
      return;
    }

    setSelectedProducts(prev => {
      const s = new Set(prev || []);
      if (isSelected) {
        pageIds.forEach(id => s.add(id));
      } else {
        // remove only ids from the current page
        pageIds.forEach(id => s.delete(id));
      }
      return Array.from(s);
    });
  };

  // Select every product matching the current filters, not just this page. The id
  // set is never materialized client-side — the backend re-resolves it from the
  // same filters at action time.
  const handleSelectAllGlobal = () => {
    setSelectAllMode(true);
    setSelectedProducts([]);
    setExcludedIds([]);
  };

  const handleUnselectAll = () => {
    setSelectAllMode(false);
    setSelectedProducts([]);
    setExcludedIds([]);
  };

  const handleExportData = async () => {
    if (!hasSelection) {
      showToast('Please select at least one product to export', 'warning');
      return;
    }

    try {
      setLoading(true);

      // Exports are generated in the background and emailed as a download link —
      // nothing comes back through this request, so there is no file to download
      // here. Matches the orders export flow.
      const result = selectAllMode
        ? await exportProductsToCSV([], {
            selectAll: true,
            filters: buildBackendFilters(),
            excludedIds,
          })
        : await exportProductsToCSV(selectedProducts);

      showToast(
        `Export queued. We'll email it to ${result.email || 'your registered email'} when it's ready — this can take a few minutes for large exports.`
      );
      handleUnselectAll();
    } catch (error) {
      console.error('Error exporting products:', error);
      showToast(error.message || 'Export failed — please try again', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (!hasSelection) {
      showToast('Please select at least one product', 'warning');
      return;
    }

    // Delete is explicit-ids only — the backend rejects select_all for it, since one
    // click would otherwise soft-delete an entire filtered catalog.
    if (action === 'delete' && selectAllMode) {
      showToast(
        'Select-all delete is not supported. Tick the specific products you want to delete.',
        'warning'
      );
      return;
    }

    const selection = selectAllMode
      ? { selectAll: true, filters: buildBackendFilters(), excludedIds }
      : { selectAll: false, filters: {}, excludedIds: [] };
    const ids = selectAllMode ? [] : selectedProducts;

    // Optimistic: mark selected items as "processing" in local state
    setProducts(prev => prev.map(p => {
      const affected = selectAllMode ? !excludedIds.includes(p.id) : selectedProducts.includes(p.id);
      return affected ? { ...p, _processing: true } : p;
    }));

    try {
      setLoading(true);
      let result;

      if (action === 'draft') {
        result = await bulkMoveToDraft(ids, 'Bulk moved to draft', selection);
        showToast(`Moved ${result.moved_count ?? effectiveSelectedCount} product(s) to draft`);
      } else if (action === 'pending_approval') {
        result = await bulkPushToPendingApproval(ids, 'Product(s) submitted for approval', selection);
        showToast(`Pushed ${result.updated_count ?? effectiveSelectedCount} product(s) to pending approval`);
      } else if (action === 'delete') {
        result = await bulkDeleteProducts(ids);
        showToast(`Deleted ${result.deleted_count ?? ids.length} product(s)`);
      }

      if (result?.errors && result.errors.length > 0) {
        showToast(`${result.errors.length} product(s) had errors — check console for details`, 'warning');
        console.warn('Bulk action row errors:', result.errors);
      }

      await loadProducts();
      handleUnselectAll();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      // Roll back optimistic update
      setProducts(prev => prev.map(p => { const { _processing, ...rest } = p; return rest; }));
      showToast(`Something went wrong, please try again`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Export admin product audit logs
  const handleExportAdminProductLogs = async () => {
    setIsExportingAdminLogs(true);
    try {
      await exportAdminProductLogs();
      showToast('Audit logs exported');
    } catch (error) {
      console.error('Error exporting admin product logs:', error);
      showToast('Failed to export audit logs', 'error');
    } finally {
      setIsExportingAdminLogs(false);
    }
  };
  
  // No early return for loading or error. The title, role badge, export button,
  // filters and bulk bar are all still usable when the rows are missing, and
  // taking them away leaves the user with nothing to act on. The table region
  // below reports both states in place.

  return (
    <div className="manageProductsContainer" style={{ scrollBehavior: 'auto' }}>
      <Toast toasts={toasts} onDismiss={dismissToast} />

      {/*
        Only shown when rows survived a failed refresh — otherwise the table
        region itself carries the message, so the two cannot contradict.
      */}
      {error && products.length > 0 && (
        <StaleDataBanner
          since={lastLoadedAt}
          onRetry={() => { setError(null); loadProducts(); }}
        />
      )}

      {selectedProduct && detailViewMode && (
        <ProductDetailView
          key={selectedProduct.id}
          product={selectedProduct}
          isReadOnly={detailViewMode === 'view' || isReadOnlyAuditor}
          onSave={handleSaveProduct}
          onClose={() => handleCloseDetailView(selectedProduct.id)}
          missingFields={selectedProduct.missing_fields || []}
        />
      )}
      
      <div className="header">
        <h1 className="title">Manage Products</h1>
        <div className="headerInfo">
          <span className="roleBadge">
            Role: {userRole ? userRole.replace('_', ' ').toUpperCase() : 'Loading...'}
          </span>
            
          {isReadOnlyAuditor && (
            <span className="readOnlyInfo">
              Read-only mode
            </span>
          )}

          <div className="shopifyActions">
            {userRole === 'admin' && (
              <button
                type="button"
                className="shopifyButton shopifyButtonSecondary"
                onClick={handleExportAdminProductLogs}
                disabled={isExportingAdminLogs}
                title="Export Product Audit Logs"
              >
                {isExportingAdminLogs ? 'Exporting...' : 'Export Audit Logs'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      <ProductFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        isReadOnly={isReadOnlyAuditor}
        userRole={userRole}
        categories={categories}
        vendors={vendors}
        showStoreLinkFilters
      />
      
      {/* Bulk Actions Bar */}
      {hasSelection && !isReadOnlyAuditor && (
        <div className="bulkActionsBar">
          <div className="bulkActionsInfo">
            <span>{effectiveSelectedCount} product(s) selected</span>
          </div>
          <div className="bulkActionsButtons">
            <button onClick={handleExportData} className="bulkActionButton success">
              Export Data
            </button>
            <button onClick={() => handleBulkAction('pending_approval')} className="bulkActionButton primary">
              Push to Pending Approval
            </button>
            <button onClick={() => handleBulkAction('draft')} className="bulkActionButton secondary">
              Move to Draft
            </button>
            {/* Delete stays explicit-ids only: a select-all delete would let one
                click soft-delete an entire filtered catalog. */}
            <button
              onClick={() => handleBulkAction('delete')}
              className="bulkActionButton danger"
              disabled={selectAllMode}
              title={selectAllMode
                ? 'Select-all delete is not supported — tick specific products to delete'
                : undefined}
            >
              Delete
            </button>
          </div>
        </div>
      )}
      
      <ProductListSection
        products={products}
        loading={loading}
        error={error}
        onRetry={() => { setError(null); loadProducts(); }}
        paginatedProducts={paginatedProducts}
        onProductAction={handleProductAction}
        isReadOnlyAuditor={isReadOnlyAuditor}
        userRole={userRole}
        userId={userId}
        productListRef={productListRef}
        startIndex={startIndex}
        endIndex={endIndex}
        filteredProducts={filteredProducts}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        selectedProducts={selectedProducts}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        effectiveSelectedCount={effectiveSelectedCount}
        onSelectAllGlobal={handleSelectAllGlobal}
        onUnselectAll={handleUnselectAll}
        onProductSelect={(productId, isSelected) => {
          console.log('ManageProducts onProductSelect called:', { productId, isSelected });
          handleProductSelect(productId, isSelected);
        }}
        onSelectAll={(isSelected) => {
          console.log('ManageProducts onSelectAll called:', { isSelected });
          handleSelectAll(isSelected);
        }}
        onRowClick={(productId) => handleProductAction('edit', productId)}
      />
      
      {showReasonModal && (
        <div className="modalOverlay" onClick={handleCancelSave}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <h2>
                {pendingSaveData?._pushToPendingApproval 
                  ? 'Push to Pending Approval' 
                  : 'Provide Reason for Changes'}
              </h2>
              <button className="modalCloseButton" onClick={handleCancelSave}>×</button>
            </div>
            <div className="modalBody">
              <p className="modalDescription">
                {pendingSaveData?._pushToPendingApproval 
                  ? 'This will save your changes and submit the product for admin approval. Please provide a reason for this submission.'
                  : 'Please provide a reason for these changes. This will be recorded in the audit history.'}
              </p>
              <label htmlFor="saveReason" className="modalLabel">
                Reason for {pendingSaveData?._pushToPendingApproval ? 'submission' : 'changes'} <span className="required">*</span>
              </label>
              <textarea
                id="saveReason"
                value={saveReason}
                onChange={(e) => setSaveReason(e.target.value)}
                placeholder={pendingSaveData?._pushToPendingApproval 
                  ? "E.g., Product is ready for review, All required information has been added, etc."
                  : "E.g., Updated product description based on vendor feedback, Corrected pricing error, etc."}
                rows="4"
                className="modalTextarea"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleCancelSave();
                  } else if (e.key === 'Enter' && e.ctrlKey && saveReason.trim()) {
                    handleConfirmSave();
                  }
                }}
              />
              <div className="modalCharCount">
                {saveReason.length} characters • Press <kbd>Ctrl+Enter</kbd> to save, <kbd>Esc</kbd> to cancel
              </div>
            </div>
            <div className="modalFooter">
              <button
                className="modalCancelButton"
                onClick={handleCancelSave}
                type="button"
              >
                Cancel
              </button>
              <button
                className="modalSaveButton"
                onClick={handleConfirmSave}
                type="button"
                disabled={!saveReason.trim()}
              >
                {pendingSaveData?._pushToPendingApproval 
                  ? 'Submit for Approval' 
                  : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default ManageProducts;